import { getListingMember } from 'app/lib/guardian';
import { UserAvatarBadge } from 'app/components/user-avatar-badge';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { getImageVariantUrl } from 'app/lib/image-variants';
import type { Listing } from 'app/types/listing';
import { useTranslation } from 'react-i18next';
import type { ColorValue } from 'react-native';
import { View } from 'react-native';
import { useMappingHelper } from '@shopify/flash-list';
import { LoanFeeTag } from './listing-card-loan-fee-tag';
import { localeAwareFormatDurationShort } from 'app/lib/date';
import { isMobileNative } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

interface ListingPostedByMembersProps {
  avatarSize: number;
  grouping?: Listing['grouping'];
  ignoreIsAuthor?: boolean;
  isAuthor: boolean;
  loanFee?: Listing['loanFee'];
  textColor?: ColorValue | string;
  user: Listing['user'];
}

function GroupedMembers({
  avatarSize,
  badgeSize,
  fixedHeight,
  grouping,
  textColor,
}: Pick<ListingPostedByMembersProps, 'avatarSize' | 'grouping' | 'textColor'> & {
  badgeSize: number;
  fixedHeight: number;
}) {
  const { t } = useTranslation('all-listings');
  const { getMappingKey } = useMappingHelper();
  const maxNumberOfAvatars = 3;
  const avatarOverlap = theme.spacing['spacing.general.x2'];
  const totalMembers = grouping?.totalOffers ?? 0;

  return (
    <Row alignItems="center" gap="spacing.general.x2" maxWidth={220} height={fixedHeight}>
      <Row alignItems="center">
        {grouping?.listings.slice(0, maxNumberOfAvatars).map((gListing: Listing, index: number) => (
          <View
            key={getMappingKey(gListing.id, index)}
            style={[
              { marginLeft: index > 0 ? -avatarOverlap : 0 },
              {
                ...(isMobileNative
                  ? {
                      shadowColor: theme.colors['text.primary'],
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.3,
                      shadowRadius: 1,
                    }
                  : {}),
                zIndex: totalMembers - index,
              },
            ]}
          >
            <PCImage
              aspectRatio={1}
              cachePolicy="memory-disk"
              height={avatarSize}
              recyclingKey={`${getListingMember(gListing).profile.picture!}-card`}
              source={{ uri: getImageVariantUrl(getListingMember(gListing).profile.picture, 'mini') }}
              style={{
                borderColor: 'white',
                borderWidth: 1,
                borderRadius: theme.borderRadii['radius.xl'],
              }}
            />
            {index <= Math.min(maxNumberOfAvatars, totalMembers) - 1 ? (
              <UserAvatarBadge
                userId={getListingMember(gListing).id}
                avatarSize={avatarSize}
                size={badgeSize}
                isAmbassador={getListingMember(gListing).isAmbassador}
                isSuper={getListingMember(gListing).isSuper}
              />
            ) : null}
          </View>
        ))}
      </Row>
      <Text ellipsizeMode="tail" flexShrink={1} numberOfLines={1} style={{ color: textColor }} variant="bodySmall">
        {totalMembers > 100 ? t('groupingMembersMany', { count: 99 }) : t('groupingMembers', { count: totalMembers })}
      </Text>
    </Row>
  );
}

function SingleMember({
  avatarSize,
  badgeSize,
  fixedHeight,
  isAuthor,
  textColor,
  user,
}: Pick<ListingPostedByMembersProps, 'avatarSize' | 'isAuthor' | 'textColor' | 'user'> & {
  badgeSize: number;
  fixedHeight: number;
}) {
  const { t } = useTranslation('all-listings');
  const fontSize = 12;
  const fullName = `${user.profile.firstName} ${user.profile.lastName}`;

  return (
    <View
      style={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: theme.spacing['spacing.general.x2'],
        width: '100%',
        minWidth: 0,
        height: fixedHeight,
      }}
    >
      {user.profile.picture ? (
        <View>
          <PCImage
            aspectRatio={1}
            cachePolicy="memory-disk"
            height={avatarSize}
            recyclingKey={`${user.profile.picture}-card`}
            source={{ uri: getImageVariantUrl(user.profile.picture, 'mini') }}
            style={{ borderRadius: theme.borderRadii['radius.xl'] }}
          />
          <UserAvatarBadge
            userId={user.id}
            avatarSize={avatarSize}
            size={badgeSize}
            isAmbassador={user.isAmbassador}
            isSuper={user.isSuper}
          />
        </View>
      ) : null}

      <Column flex={1} minWidth={0}>
        <Text
          ellipsizeMode="tail"
          numberOfLines={1}
          fontSize={14}
          fontWeight={500}
          style={{ color: textColor, top: !user.trustPoints && !user.lastSeenAt ? fontSize / 2 : 0 }}
          variant="bodyBase"
        >
          {fullName}
        </Text>
        <Row alignItems="center" gap="spacing.general.x1" minHeight={fontSize}>
          {!isAuthor && user.trustPoints ? (
            <Text
              fontSize={fontSize}
              color="text.tertiary"
              numberOfLines={1}
              style={{ width: '100%' }}
              variant="bodyBase"
            >
              {`👍 (${user.trustPoints.toFixed(0)})`}
              {user.lastSeenAt ? ' | ' : ''}
              {user.lastSeenAt
                ? t('lastActiveAgoShort', {
                    ago: localeAwareFormatDurationShort(new Date(user.lastSeenAt)),
                    ns: 'common',
                  })
                : ''}
            </Text>
          ) : !isAuthor && user.lastSeenAt ? (
            <Text
              fontSize={fontSize}
              color="text.tertiary"
              numberOfLines={1}
              style={{ width: '100%' }}
              variant="bodyBase"
            >
              {t('lastActiveAgoShort', {
                ago: localeAwareFormatDurationShort(new Date(user.lastSeenAt)),
                ns: 'common',
              })}
            </Text>
          ) : null}
        </Row>
      </Column>
    </View>
  );
}

function ListingPostedByMembers(props: ListingPostedByMembersProps) {
  const { avatarSize, ignoreIsAuthor, isAuthor, loanFee, textColor, grouping, user } = props;
  const badgeSize = Math.round(avatarSize * 0.65);

  const fixedHeight = isMobileNative ? 30 : Math.max(avatarSize, 30);
  const isGroupedListing = Boolean(!isAuthor && grouping && grouping.totalOffers > 1);

  if (isGroupedListing) {
    return (
      <GroupedMembers
        avatarSize={avatarSize}
        badgeSize={badgeSize}
        fixedHeight={fixedHeight}
        grouping={grouping}
        textColor={textColor}
      />
    );
  }

  if (isAuthor && !ignoreIsAuthor) return <LoanFeeTag loanFee={loanFee} />;

  return (
    <SingleMember
      avatarSize={avatarSize}
      badgeSize={badgeSize}
      fixedHeight={fixedHeight}
      isAuthor={isAuthor}
      textColor={textColor}
      user={user}
    />
  );
}
export { ListingPostedByMembers };
